import { Bars3Icon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { json, Link, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import { addUser, removeUser } from "../../slice/userSlice";
import { persistor } from "../../store/store";
import CustomButton from "../components/Button";
import profile from "../../assets/profile/profile.png";
import profileAdmin from "../../assets/profile/profile-admin.jpg";
import { Button } from "reactstrap";
import AuthService from "../../service/AuthService";
import Loader from "../loader/Loader";
import { jwtDecode } from "jwt-decode";
import { clearCart } from "../../slice/cartSlice";

const Navbar = () => {
  const user = useSelector((state) => state.user.user);
  const cart = useSelector((state) => state.cart);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(
    user?.access_token ? true : false,
  );
  const [showNavmenu, setShowNavmenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isUser, setIsUser] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [visible, setVisible] = useState(true);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const MenuListItems = isLoggedIn
    ? [
        {
          id: 1,
          title: "Home",
          links: "/",
        },
        // {
        //   id: 2,
        //   title: "Subscribed Sessions",
        //   links: "/subscribedSessions",
        // },
        {
          id: 3,
          title: "Subscribed Tutorials",
          links: "/subscribedTutorials",
        },
        // {
        //   id: 4,
        //   title: "More Sessions",
        //   links: "/sessions",
        // },
        // {
        //   id: 5,
        //   title: "Plans",
        //   links: "/pricing",
        // },
      ]
    : [
        {
          id: 1,
          title: "Home",
          links: "/",
        },
        // {
        //   id: 2,
        //   title: "Sessions",
        //   links: "/sessions",
        // },
        // {
        //   id: 3,
        //   title: "Plans",
        //   links: "/pricing",
        // },
      ];

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      const isScrollingUp = prevScrollPos > currentScrollPos;
      const isAtTop = currentScrollPos < 10;

      setVisible(isScrollingUp || isAtTop);
      setPrevScrollPos(currentScrollPos);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [prevScrollPos]);

  //logout user if access token is expired
  useEffect(async () => {
    setIsLoading(true);
    if (!user?.access_token) {
      setIsLoggedIn(false);
    } else if (
      user?.access_token &&
      AuthService.isTokenExpired(user.access_token)
    ) {
      //refresh token
      try {
        setIsLoading(true);
        const response = await AuthService.refresh(user.refresh_token);
        // Assuming response.data contains user info
        const userData = jwtDecode(response.data.access_token);
        dispatch(
          addUser({
            ...response.data,
            exp: userData.exp,
            iat: userData.iat,
            sub: userData.sub,
            ...userData.user,
          }),
        );
      } catch (err) {
        if (err.response) {
          switch (err.response.status) {
            case 401:
              break;
            case 404:
              break;
            default:
              break;
          }
        }
        // remove user
        dispatch(removeUser());
        dispatch(clearCart());
        persistor.purge();
        setTimeout(() => {
          navigate("/");
        }, 200);
        toast.error("Session expired, please log in again", { duration: 4000 });
      } finally {
        setIsLoading(false);
      }
    }
    setIsLoading(false);
  }, [user?.access_token]);

  useEffect(() => {
    const handleClickOutside = () => {
      setShowNavmenu(false);
    };

    if (showNavmenu === true) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showNavmenu]);

  return (
    <>
      <nav
        className={`fixed w-full bg-white/95 backdrop-blur-md border-b border-[#662d91]/10 transition-transform duration-300 z-50 ${
          visible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        {/* ── Mobile: Logo row (centered, only on small screens) ── */}
        <div className="md:hidden flex justify-center py-2 border-b border-[#662d91]/5">
          <Link
            to="/"
            onClick={() => { if (window.location.pathname === "/") window.location.reload(); }}
          >
            <img src={logo} className="h-10 md:h-12" alt="Nine and Fine" />
          </Link>
        </div>

        {/* ── Main bar: single row on md+, second row on mobile ── */}
        <div className="max-w-screen-xl mx-auto px-3 md:px-6">
          <div className="flex items-center justify-between h-12 md:h-[70px]">

            {/* Desktop Logo (hidden on mobile since it's above) */}
            <Link
              to="/"
              className="hidden md:flex items-center flex-shrink-0"
              onClick={() => { if (window.location.pathname === "/") window.location.reload(); }}
            >
              <img src={logo} className="h-[70px]" alt="Lifecell Wellness" />
            </Link>

            {/* Mobile: Hamburger (left side) */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg text-[#662d91] hover:bg-[#662d91]/5 transition-colors"
              onClick={() => setShowNavmenu((v) => !v)}
            >
              <span className="sr-only">Open main menu</span>
              {showNavmenu ? (
                <XMarkIcon className="w-5 h-5" />
              ) : (
                <Bars3Icon className="w-5 h-5" />
              )}
            </button>

            {/* Desktop Navigation Links (center) */}
            <ul className="hidden md:flex items-center list-none pl-0 mb-0 space-x-1">
              {MenuListItems.map((item) => (
                <li key={item.id}>
                  <Link
                    to={item.links}
                    onClick={() => {
                      if (window.location.pathname === item.links) window.location.reload();
                    }}
                    className={`block px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-sm md:text-base font-semibold transition-all duration-200 text-decoration-none ${
                      window.location.pathname === item.links
                        ? "text-[#662d91] bg-[#662d91]/5"
                        : "text-gray-600 hover:text-[#662d91] hover:bg-[#662d91]/5"
                    }`}
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Right side: Cart + Profile / Auth buttons */}
            <div className="flex items-center space-x-2">
              {isLoggedIn ? (
                <>
                  {/* Cart */}
                  <Link to="/cart" className="relative p-2 rounded-lg hover:bg-[#662d91]/5 transition-colors">
                    <svg className="w-6 h-6 md:w-10 md:h-10 text-[#662d91]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {(cart.tutorialItems?.length || 0) + (cart.sessionItems?.length || 0) > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-[#a247e6] text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                        {(cart.tutorialItems?.length || 0) + (cart.sessionItems?.length || 0)}
                      </span>
                    )}
                  </Link>

                  {/* Profile */}
                  <div className="relative">
                    <button
                      onBlur={() => setTimeout(() => setShowUserMenu(false), 150)}
                      type="button"
                      className={`flex rounded-full p-0.5 ring-2 transition-all ${
                        showUserMenu ? "ring-[#662d91]" : "ring-transparent hover:ring-[#662d91]/30"
                      }`}
                      onClick={() => setShowUserMenu((v) => !v)}
                    >
                      <img
                        className="w-8 h-8 md:w-[60px] md:h-[60px] rounded-full object-cover"
                        src={
                          user?.role?.roleId === "ROLE_ADMIN" ||
                          user?.role?.roleId === "ROLE_TRAINER" ||
                          user?.role?.roleId === "ROLE_TUTORIAL_OWNER" ||
                          user?.role?.roleId === "ROLE_TL"
                            ? profileAdmin
                            : profile
                        }
                        alt="user photo"
                      />
                    </button>

                    {/* Dropdown menu */}
                    <div
                      style={{
                        transition: "all 250ms cubic-bezier(0.16, 1, 0.3, 1)",
                        opacity: showUserMenu ? 1 : 0,
                        transform: showUserMenu ? "translateY(0) scale(1)" : "translateY(-8px) scale(0.96)",
                        pointerEvents: showUserMenu ? "auto" : "none",
                      }}
                      className="absolute right-0 top-12 z-[100] min-w-[220px] bg-white rounded-2xl shadow-[0_12px_40px_rgba(102,45,145,0.12)] border border-gray-100 overflow-hidden"
                    >
                      <div className="px-4 py-3 bg-[#fcf3ff] border-b border-[#662d91]/5">
                        <span className="block text-sm font-bold text-[#2a133c] truncate">
                          {user?.name || "User"}
                        </span>
                        <span className="block text-xs text-gray-500 truncate mt-0.5">
                          {user?.email}
                        </span>
                      </div>
                      <ul className="py-1.5 pl-0 list-none mb-0">
                        {(user?.role?.roleId === "ROLE_ADMIN" ||
                          user?.role?.roleId === "ROLE_TRAINER" ||
                          user?.role?.roleId === "ROLE_TUTORIAL_OWNER" ||
                          user?.role?.roleId === "ROLE_TL") && (
                          <li className="px-1.5">
                            <Link
                              to={
                                (user?.role?.roleId === "ROLE_ADMIN" || user?.role?.roleId === "ROLE_TL")
                                  ? "/admin"
                                  : user?.role?.roleId === "ROLE_TUTORIAL_OWNER"
                                    ? "/admin/report/tutorial-logs"
                                    : "/admin/sessions"
                              }
                              className="block px-3 py-2 text-sm md:text-base text-gray-700 font-medium rounded-lg hover:bg-[#662d91]/5 hover:text-[#662d91] transition-all text-decoration-none"
                              onClick={() => setShowUserMenu(false)}
                            >
                              {(user?.role?.roleId === "ROLE_ADMIN" || user?.role?.roleId === "ROLE_TL")
                                ? "Admin Dashboard"
                                : user?.role?.roleId === "ROLE_TUTORIAL_OWNER"
                                  ? "Tutorial Log"
                                  : "Program Sessions"}
                            </Link>
                          </li>
                        )}
                        {isLoggedIn && user?.role?.roleId === "ROLE_CUSTOMER" && (
                          <>
                            <li className="px-1.5">
                              <Link to="/subscription" className="block px-3 py-2 text-sm md:text-base text-gray-700 font-medium rounded-lg hover:bg-[#662d91]/5 hover:text-[#662d91] transition-all text-decoration-none" onClick={() => setShowUserMenu(false)}>
                                Subscription - {user.customer?.customerSubscription?.subscriptionPlan?.subscriptionPlanId === 1 ? "Plus" : "Basic"}
                              </Link>
                            </li>
                            <li className="px-1.5">
                              <Link to="/customer/profile" className="block px-3 py-2 text-sm md:text-base text-gray-700 font-medium rounded-lg hover:bg-[#662d91]/5 hover:text-[#662d91] transition-all text-decoration-none" onClick={() => setShowUserMenu(false)}>
                                My Profile
                              </Link>
                            </li>
                            <li className="px-1.5">
                              <Link to="/myorders" className="block px-3 py-2 text-sm md:text-base text-gray-700 font-medium rounded-lg hover:bg-[#662d91]/5 hover:text-[#662d91] transition-all text-decoration-none" onClick={() => setShowUserMenu(false)}>
                                My Orders
                              </Link>
                            </li>
                          </>
                        )}
                      </ul>
                      <div className="p-2.5 border-t border-gray-100">
                        <CustomButton
                          type="button"
                          styleType="cta-one w-full text-xs py-2"
                          title="Sign out"
                          handleClick={async (e) => {
                            e.preventDefault();
                            dispatch(removeUser());
                            dispatch(clearCart());
                            await persistor.purge();
                            setTimeout(() => {
                              navigate("/");
                              window.location.reload();
                            }, 200);
                            toast.success("Signed out successfully", { duration: 4000 });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-1.5">
                  <Link to="/login">
                    <CustomButton title="Log in" type="button" styleType="cta-two" />
                  </Link>
                  <Link to="/register">
                    <CustomButton title="Sign Up" type="button" styleType="cta-one" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Mobile nav menu (slides down) ── */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            showNavmenu ? "max-h-96 border-t border-[#662d91]/5" : "max-h-0"
          }`}
        >
          <ul className="list-none pl-0 mb-0 py-2 px-4">
            {MenuListItems.map((item) => (
              <li key={item.id}>
                <Link
                  to={item.links}
                  onClick={() => {
                    setShowNavmenu(false);
                    if (window.location.pathname === item.links) window.location.reload();
                  }}
                  className={`block py-2.5 px-3 rounded-lg text-sm md:text-base font-semibold transition-all text-decoration-none ${
                    window.location.pathname === item.links
                      ? "text-[#662d91] bg-[#662d91]/5"
                      : "text-gray-600 hover:text-[#662d91] hover:bg-[#662d91]/5"
                  }`}
                >
                  {item.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Spacer — compact on desktop, taller on mobile to account for logo row */}
      <div className="h-[5.5rem] md:h-[70px]"></div>
      {isLoading && <Loader />}
    </>
  );
};

export default Navbar;
